// @flow

import type { Trip } from '@ta/trips.trip-types';
import * as TripTypes from '@ta/trips.trip-types';
import * as React from 'react';
import type { InvalidationPredicateResponse } from '@ta/platform.urql';
import { usePaginatedQuery } from '@ta/platform.urql';
import {
  GetUserAuthZInfo as GetUserAuthZInfoQuery,
  type TripDetailSTAT,
  TripDetailStatQuery,
  type TripDetailStatQueryData,
  type TripDetailStatQueryVariables,
} from '@ta/trips.graphql';
import { toReference } from '@ta/trips.trip-util';
import { featureIsEnabled } from '@ta/common.features';
import { useQuery } from 'urql';
import { Visitor } from '@ta/lithium.context';

export type UserTripData = {|
  tripList: Trip[],
  saveSet: Set<number>,
  canMakePublicTrip: boolean,
|};

const combineTripLists = (prev: TripDetailStatQueryData, next: ?TripDetailStatQueryData) => {
  if (!next) {
    return prev;
  }

  const prevTrips = prev.paginatedTrips?.trips || [];
  const nextTrips = next.paginatedTrips?.trips || [];

  const result = {
    paginatedTrips: prev.paginatedTrips
      ? {
          trips: [...prevTrips, ...nextTrips],
          hasMore: !!next.paginatedTrips?.hasMore,
          __typename: prev.paginatedTrips.__typename,
        }
      : next.paginatedTrips,
    savedTrips: prev.savedTrips, // don't bother merging savedTrips they are not paged
  };
  return result;
};

export type UserTripsResponse = {
  fetching: boolean,
  error: boolean,
  data: UserTripData,
  hasMore: boolean,
  loadMore: () => Promise<*>,
};

/**
 * A temporary adapter from TripDetailSTAT to Types.Trip
 */
const convertToTrip = (trip: TripDetailSTAT): Trip => ({
  __typename: trip.__typename,
  id: trip.id || 0,
  title: trip.title || undefined,
  status: trip.status || undefined,
  photoId: trip.photoId || undefined,
  photo: trip.photo || {},
  route: { url: (trip.route && trip.route.url) || undefined },
  actionPermissions: {
    canAddItem: !!trip.actionPermissions?.canAddItem,
  },
});

const useUserTrips = (saveObject: TripTypes.TripSaveObject): UserTripsResponse => {
  const invalidatePredicate = (id: ?string, type: ?string): InvalidationPredicateResponse => {
    if (!id || !type) {
      return 'invalidateAndRefetch';
    }
    if (type === saveObject.type && id === String(saveObject.id)) {
      return 'invalidateAndRefetch';
    }
    return 'validate';
  };

  const { userId } = React.useContext(Visitor);

  const variables: TripDetailStatQueryVariables = {
    offset: 0,
    limit: 10,
    userId: userId || '',
    reference: toReference(saveObject),
    loadSavedTrips: true,
  };
  const [tripListResult, loadMore] = usePaginatedQuery({
    query: TripDetailStatQuery,
    variables,
    combineData: combineTripLists,
    showFetchingAfterFirstQuery: true,
    invalidationPredicates: {
      createTrip: () => 'invalidateAndRefetch',
      cloneTrip: () => 'invalidateAndRefetch',
      tripsDelete: () => 'invalidateAndRefetch',
      // invalidate savedTrips list for the reference
      addItemToTrip: ({ reference }) => invalidatePredicate(reference.id, reference.type),
      moveTripItem: ({ reference }) => invalidatePredicate(reference?.id, reference?.type),
      tripsRemoveItem: ({ reference }) => invalidatePredicate(reference?.id, reference?.type),
      tripsRemoveReference: ({ reference }) => invalidatePredicate(reference?.id, reference?.type),
    },
  });

  const skipAuthCheck = featureIsEnabled('trips_skip_check_whitelist_public_trips');
  const [authResult] = useQuery({
    query: GetUserAuthZInfoQuery,
    variables: {},
    pause: skipAuthCheck,
  });

  const trips = tripListResult.data?.paginatedTrips?.trips || [];
  const convertedTrips: Trip[] = trips
    .slice()
    .filter(Boolean)
    .map(convertToTrip);
  const hasMore = !!tripListResult.data?.paginatedTrips?.hasMore;
  const savedTrips = tripListResult.data?.savedTrips || [];

  const saveSet = new Set(savedTrips.map(t => t?.id).filter(Boolean));
  const getMoreTrips = hasMore
    ? (): Promise<*> =>
        loadMore({
          offset: trips?.length || 0,
          loadSavedTrips: false,
        })
    : () => Promise.resolve();

  const canMakePublicTrip = skipAuthCheck || authResult.data?.authzInfo?.canMakePublicTrip;

  return {
    fetching: tripListResult.fetching || authResult.fetching,
    error: !!tripListResult.error,
    data: {
      tripList: convertedTrips,
      saveSet,
      canMakePublicTrip,
    },
    hasMore,
    loadMore: getMoreTrips,
  };
};

export default useUserTrips;
